import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, ExternalLink, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_KEY = 'keyMadeReferenceLinks';
const MIGRATED_FLAG = 'keyMadeReferenceLinksMigrated';

/**
 * DESIGN PASS 21 — the firm's resource library.
 *
 * These links used to live in localStorage: one browser, one person, invisible
 * on a phone and to everyone else at the firm. They are tenant data now — a
 * county parcel lookup or a city zoning portal belongs to the firm, not to
 * whoever happened to type it — stored as Knowledge Base link entries.
 *
 * Every row is a full-width target with a 44px reach, because half this
 * research happens standing in a driveway.
 */
export default function ReferenceToolsBar({
  darkMode = false,
  compact = false,
  showDefaultLinks = true,
  className = '',
  fieldKey = null,
  title = 'Reference Tools',
}) {
  const [own, setOwn] = useState([]);
  const [shipped, setShipped] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', url: '', category: '' });
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/resource-links`);
      setOwn(res.data.links || []);
      setShipped(res.data.shipped || []);
    } catch {
      setOwn([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const migrate = async () => {
      // One browser at a time: whatever this person added locally becomes the
      // firm's, then the local copy is retired. Nothing is dropped.
      const stored = localStorage.getItem(LEGACY_KEY);
      if (stored && !localStorage.getItem(MIGRATED_FLAG)) {
        try {
          const links = JSON.parse(stored) || [];
          if (links.length) {
            const res = await axios.post(`${API}/resource-links/migrate`, {
              links: links.map((l) => ({ name: l.label || l.name, url: l.url })),
            });
            if (res.data.imported) {
              toast.success(`${res.data.imported} of your links are now shared with the firm`);
            }
          }
          localStorage.setItem(MIGRATED_FLAG, new Date().toISOString());
        } catch {
          /* keep the local copy if the import fails — it is the only copy */
        }
      }
      load();
    };
    migrate();
  }, [load]);

  const handleAddLink = async () => {
    if (!newLink.label?.trim() || !newLink.url?.trim()) {
      toast.error('A link needs a name and a web address');
      return;
    }
    try {
      const res = await axios.post(`${API}/resource-links`, {
        title: newLink.label.trim(),
        url: newLink.url.trim(),
        category: newLink.category.trim() || 'General',
        field_key: fieldKey,
      });
      toast[res.data.duplicate ? 'info' : 'success'](
        res.data.duplicate ? 'The firm already has that link' : 'Saved for everyone at the firm',
      );
      setNewLink({ label: '', url: '', category: '' });
      setShowAddDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save that link');
    }
  };

  const handleRemoveLink = async (link) => {
    try {
      await axios.delete(`${API}/resource-links/${link.id}`);
      toast.success(link.shipped ? 'Hidden for the firm' : 'Link removed');
      load();
    } catch {
      toast.error('Could not remove that link');
    }
  };

  const allLinks = [...(showDefaultLinks ? shipped : []), ...own];

  if (compact && !isExpanded) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsExpanded(true)}
        className={`${darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A] text-[#E5E2DD]' : ''} ${className}`}
        data-testid="reference-tools-open"
      >
        <BookOpen className="w-4 h-4 mr-2" />
        {title}
      </Button>
    );
  }

  // DP21 — in a 200px rail the names truncate to eight characters and the
  // floating avatar sits on top of the header, so the compact panel opens as a
  // fixed overlay at a readable width instead of inside the rail.
  const shellClass = compact
    ? 'fixed left-24 bottom-24 z-[200] w-[320px] max-w-[calc(100vw-7rem)] max-h-[64vh] overflow-y-auto shadow-lg'
    : '';

  return (
    <div
      className={`border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[color:var(--km-rule)] bg-[color:var(--km-paper)]'} ${shellClass} ${className}`}
      data-testid="reference-tools"
    >
      <div className={`flex items-center justify-between px-3 py-2 border-b ${darkMode ? 'border-[#1A1A1A]' : 'border-[color:var(--km-rule-soft)]'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium truncate">{title}</span>
          <span className="text-xs text-[color:var(--km-ink-2)]" data-testid="reference-tools-count">
            {allLinks.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setShowAddDialog(true)}
                  data-testid="reference-tools-add">
            <Plus className="w-4 h-4" />
          </Button>
          {compact && (
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded(false)}
                    data-testid="reference-tools-close">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <ul className="divide-y divide-[color:var(--km-rule-soft)]" data-testid="reference-tools-list">
        {loading && (
          <li className="px-3 py-3 text-xs text-[color:var(--km-ink-2)]">Loading…</li>
        )}
        {!loading && allLinks.length === 0 && (
          <li className="px-3 py-3 text-xs text-[color:var(--km-ink-2)]" data-testid="reference-tools-empty">
            No links yet. Add the county parcel lookup, the zoning portal, the flood map —
            whatever this job needs.
          </li>
        )}
        {allLinks.map((link) => (
          <li key={link.id} className="flex items-stretch" data-testid={`reference-link-${link.id}`}>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-0 flex items-center gap-2 px-3 min-h-[44px] hover:bg-[color:var(--km-paper-2)]"
            >
              <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 text-[color:var(--km-ink-2)]" />
              <span className="text-sm truncate">{link.title}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wider text-[color:var(--km-ink-2)] flex-shrink-0">
                {link.shipped ? 'Included' : link.category}
              </span>
            </a>
            <button
              type="button"
              onClick={() => handleRemoveLink(link)}
              className="px-3 text-[color:var(--km-ink-2)] hover:text-[color:var(--km-late)]"
              title={link.shipped ? 'Hide for the firm' : 'Remove'}
              data-testid={`reference-link-remove-${link.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="reference-tools-dialog">
          <DialogHeader>
            <DialogTitle>Add a link for the firm</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="refLinkName">Name</Label>
              <Input id="refLinkName" value={newLink.label}
                     onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                     placeholder="Travis County parcel lookup"
                     data-testid="reference-tools-name-input" />
            </div>
            <div>
              <Label htmlFor="refLinkUrl">Web address</Label>
              <Input id="refLinkUrl" value={newLink.url}
                     onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                     placeholder="traviscad.org"
                     data-testid="reference-tools-url-input" />
            </div>
            <div>
              <Label htmlFor="refLinkCat">Category</Label>
              <Input id="refLinkCat" value={newLink.category}
                     onChange={(e) => setNewLink({ ...newLink, category: e.target.value })}
                     placeholder="Zoning, Flood, Utilities — your own words"
                     data-testid="reference-tools-category-input" />
            </div>
            <p className="text-xs text-[color:var(--km-ink-2)]">
              Everyone at the firm will see this, on every device.
            </p>
            <Button onClick={handleAddLink} className="w-full" data-testid="reference-tools-save">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
